import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { trapTab } from '../utils/focusTrap';
import { reportTab } from '../utils/issueReports';

const TOKEN_KEY = 'mb_token';

/**
 * Self-service account deletion -- App Store Review Guideline 5.1.1(v): an app
 * that lets people create an account must let them delete it from inside the
 * app. PITRIOT's /survey page does exactly that, publicly and by default.
 *
 * The dialog states plainly what goes and what stays, because "delete" means
 * something narrower here than a person will assume: research records the
 * institute relies on are KEPT, de-identified, rather than destroyed. Saying so
 * before the click is what makes that honest.
 *
 * The password is asked for again even though the caller is signed in. A phone
 * left unlocked on a bench should not be one tap away from an irreversible act.
 *
 * A modal dialog in full: focus moves to its title as it opens (so the warning
 * is read first), Escape closes it through `onClose`, and Tab and Shift+Tab
 * cycle inside it -- aria-modal tells assistive technology the page behind is
 * inert, so focus must not wander there either. `onClose` puts focus back on
 * whatever opened it.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function DeleteAccountModal({ onClose, returnTo = '/login' }) {
  const { t } = useTranslation(['account', 'register']);
  const [password, setPassword] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const titleRef = useRef(null);
  const busyRef = useRef(false);
  busyRef.current = busy;
  // Set while closing, so the focus trap does not pull back the focus that
  // `onClose` returns to the page.
  const closingRef = useRef(false);
  const close = () => {
    if (busyRef.current) return;
    closingRef.current = true;
    onClose?.();
  };

  useEffect(() => {
    titleRef.current?.focus();
    // Focus that lands outside (a click on the dim backdrop, a stray
    // programmatic focus) comes back to the dialog.
    const keepInside = (e) => {
      const dialog = dialogRef.current;
      if (closingRef.current) return;
      // Report an issue's tab and dialog may hold focus: a problem met here
      // is reported from here.
      if (e.target?.closest?.('[data-report-issue], [data-report-issue-tab]')) return;
      if (dialog && !dialog.contains(e.target)) titleRef.current?.focus();
    };
    document.addEventListener('focusin', keepInside);
    return () => document.removeEventListener('focusin', keepInside);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = [...(dialogRef.current?.querySelectorAll(FOCUSABLE) || [])];
    if (!items.length) { e.preventDefault(); return; }
    // Report an issue's tab is part of the cycle.
    const tab = reportTab();
    if (tab && !tab.hidden) { trapTab(dialogRef.current, e, { extra: tab }); return; }
    const first = items[0];
    const last = items[items.length - 1];
    const at = items.indexOf(document.activeElement);
    if (e.shiftKey && at <= 0) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && at === items.length - 1) { e.preventDefault(); first.focus(); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!understood || !password) return;
    setError('');
    setBusy(true);
    try {
      await api.post('/auth/delete-account', { password });
      try { localStorage.removeItem(TOKEN_KEY); } catch (_) { /* storage disabled */ }
      // A full navigation, not a router push: every in-memory trace of the
      // session -- context, cached lists, the axios instance's state -- goes.
      window.location.href = `${returnTo}?accountDeleted=1`;
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 429) {
        // Five wrong passwords in a quarter hour (change-password counts
        // toward the same limit). A wait, not a lockout.
        const minutes = Math.max(1, Math.ceil((Number(data.retry_after) || 60) / 60));
        setError(t('register:errors.rate_limited', { minutes }));
      } else {
        setError(t(
          data.code === 'wrong_password' ? 'wrongPassword'
            : data.code === 'last_collection_manager' ? 'lastManager'
              : data.code === 'last_system_admin' ? 'lastSystemAdmin'
                : 'failed'));
      }
      setBusy(false);
    }
  };

  return (
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="delete-account-title"
         style={styles.overlay} onKeyDown={onKeyDown}>
      <form style={styles.card} onSubmit={submit}>
        <h2 id="delete-account-title" ref={titleRef} tabIndex={-1} style={styles.title}>{t('deleteTitle')}</h2>
        <p style={styles.warning}>{t('deleteIntro')}</p>

        <h3 style={styles.h3}>{t('whatIsRemoved')}</h3>
        <ul style={styles.list}>
          <li>{t('removedIdentity')}</li>
          <li>{t('removedSightings')}</li>
          <li>{t('removedAccess')}</li>
        </ul>

        <h3 style={styles.h3}>{t('whatIsKept')}</h3>
        <p style={styles.body}>{t('keptRecords')}</p>

        <label style={styles.label} htmlFor="delete-account-password">{t('passwordLabel')}</label>
        <input id="delete-account-password" type="password" autoComplete="current-password"
               style={styles.input} value={password} onChange={e => setPassword(e.target.value)} title={t('tip.deletePassword')} />

        <label style={styles.check}>
          <input type="checkbox" checked={understood} onChange={e => setUnderstood(e.target.checked)} title={t('tip.deleteUnderstand')} />
          <span>{t('understand')}</span>
        </label>

        {error && <p role="alert" style={styles.error}>{error}</p>}

        <div style={styles.row}>
          <button type="button" style={styles.secondary} onClick={close} disabled={busy} title={t('tip.deleteCancel')}>
            {t('cancel')}
          </button>
          <button type="submit" style={{ ...styles.danger, opacity: (!understood || !password || busy) ? 0.5 : 1 }}
                  disabled={!understood || !password || busy} title={t('tip.deleteConfirm')}>
            {busy ? t('deleting') : t('confirm')}
          </button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
             alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  card: { background: 'var(--c-surface)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480,
          maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' },
  title: { margin: '0 0 10px', fontSize: 18, color: 'var(--c-danger-3-fg)' },
  warning: { fontSize: 14, fontWeight: 600, color: 'var(--c-danger-3-fg)', background: 'var(--c-danger-soft-4)',
             border: '1px solid var(--c-danger-border)', borderRadius: 6, padding: '8px 10px' },
  h3: { fontSize: 14, margin: '14px 0 4px' },
  list: { fontSize: 13, color: 'var(--c-text-body-fg)', paddingInlineStart: 20, margin: 0, lineHeight: 1.6 },
  body: { fontSize: 13, color: 'var(--c-text-body-fg)', margin: 0, lineHeight: 1.6 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginTop: 16, marginBottom: 4 },
  input: { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '9px 10px',
           border: '1px solid var(--c-border-strong)', borderRadius: 6 },
  check: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginTop: 12 },
  error: { color: 'var(--c-danger-2-fg)', fontSize: 13, fontWeight: 600, marginTop: 10 },
  row: { display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' },
  secondary: { background: 'var(--c-surface)', color: 'var(--c-text-body-fg)', border: '1px solid var(--c-border-strong)', borderRadius: 6,
               padding: '9px 16px', cursor: 'pointer' },
  danger: { background: 'var(--c-danger-2)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
            padding: '9px 16px', fontWeight: 600, cursor: 'pointer' },
};
